// GenerateImageToolUI — branded tool-UI for the agent's `generateImage` tool
// call, rendered inline in the Aidea Assistant chat thread.
//
// New behaviour (popup-handoff): the tool NO LONGER generates an image. Its
// result is `{ prompt, openGenerator: true }` — an enhanced prompt plus a flag
// telling the client to open the studio's existing "Add Image → AI Generate"
// popup pre-filled. On the COMPLETE state we dispatch the cross-component
// window event `aidea:openImageGenerator` exactly ONCE (guarded by a useRef so
// re-renders don't re-open) and render a small branded confirmation card. The
// popup owns model selection (default Nano Banana Pro), styles, aspect, the
// actual generation, and adding the result to the scene — so there is no
// <img> and no "Add to scene" here anymore.
//
// Registration: makeAssistantToolUI({ toolName: "generateImage", render })
// registers `render` for every "generateImage" tool-call part while mounted in
// <AssistantRuntimeProvider>. We render <GenerateImageToolUI /> once in
// ChatPanel (alongside RecommendFormatsToolUI).
//
// Result shape: the backend returns a JSON STRING `{"prompt":"…","openGenerator":
// true}` (or `{"error":"…"}` on failure). Some transports may hand it back
// already parsed, so parseImageResult is defensive on both axes and never throws.
//
// Styling follows the chat scoping contract: every utility is `tw-`-prefixed and
// only takes effect under `.aidea-chat-root`. Brand tokens via CSS vars. No emoji.

import React, { useCallback, useEffect, useRef } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";

import { SpinnerIcon, ImageGenIcon, AlertIcon } from "../icons";
import { dispatchOpenAIPopup, shouldAutoOpenOnce } from "../aiPopupGateway";
import { OpenPopupButton } from "./OpenPopupButton";
import { mapAiProviderErrorMessage } from "../../../../common/ai_provider_errors.es6";

// Parse the tool result into `{ prompt, openGenerator } | { error } | null`. The
// backend sends a JSON string; some transports may hand it back already parsed.
// Be defensive (string-vs-object) and never throw.
function parseImageResult(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  if (typeof data.error === "string" && data.error.trim()) {
    return { error: data.error.trim() };
  }
  const prompt = typeof data.prompt === "string" ? data.prompt.trim() : "";
  const openGenerator = data.openPopup === true || data.openGenerator === true;
  const settings = data.settings && typeof data.settings === "object" ? data.settings : {};
  if (!openGenerator && !prompt) return null;
  return { prompt, settings, openGenerator };
}

// Small violet-tint header chip, e.g. "Image".
function HeaderChip({ label }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <ImageGenIcon size={12} />
      {label}
    </span>
  );
}

// Card chrome shared by every state so the tool always looks like one card.
function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[440px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

// Running state: spinner + "Preparing the image generator…" (+ the prompt if it
// has streamed). Shown while the tool call is still running.
function RunningState({ prompt }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
        <SpinnerIcon size={15} className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          {prompt ? (
            <>
              Preparing the image generator for{" "}
              <span className="tw-font-medium tw-text-[var(--aidea-fg)]">"{prompt}"</span>…
            </>
          ) : (
            "Preparing the image generator…"
          )}
        </span>
      </div>
    </Card>
  );
}

// Error / parse-failure fallback — still a branded card so it reads as the same
// tool, just with nothing to show.
function ErrorState({ message }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Image" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
        <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]" />
        <span className="tw-min-w-0">
          {mapAiProviderErrorMessage(message) || message || "Couldn’t prepare the image generator."}
        </span>
      </div>
    </Card>
  );
}

// Opened state: dispatches via the gateway exactly once (useRef guard),
// then shows a short branded confirmation. No <img>, no Add-to-scene — the popup
// owns generation + commit.
function OpenedState({ prompt, settings, toolCallId }) {
  const dispatchedRef = useRef(false);

  const doOpen = useCallback(
    () => dispatchOpenAIPopup({ feature: "image", prompt, settings }),
    [prompt, settings]
  );

  // Auto-open ONCE per tool call (suppress re-open on chat close/reopen + restore).
  useEffect(() => {
    if (dispatchedRef.current) return;
    dispatchedRef.current = true;
    const key = toolCallId || `image:${prompt || ""}`;
    if (!shouldAutoOpenOnce(key)) return undefined;
    const cancel = doOpen();
    return cancel;
  }, [doOpen, toolCallId, prompt]);

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Image" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
        <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
          <ImageGenIcon size={16} />
        </span>
        <div className="tw-min-w-0">
          <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">
            Opened the image generator{settings?.style ? ` — ${settings.style}` : ""}
            {settings?.aspectRatio ? ` · ${settings.aspectRatio}` : ""}. Pick a model and click
            Generate.
          </p>
          {prompt && (
            <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-italic tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
              "{prompt}"
            </p>
          )}
          <OpenPopupButton label="Open image generator" onClick={doOpen} />
        </div>
      </div>
    </Card>
  );
}

export const GenerateImageToolUI = makeAssistantToolUI({
  toolName: "generateImage",
  render: ({ args, result, status, toolCallId }) => {
    const argPrompt = args && typeof args.prompt === "string" ? args.prompt : "";

    // While the tool call is still running (model streaming args, or the tool
    // executing) show the preparing affordance.
    if (status && status.type === "running") {
      return <RunningState prompt={argPrompt} />;
    }

    const parsed = parseImageResult(result);
    if (!parsed) {
      // Completed (or incomplete) with nothing parseable — graceful fallback.
      return <ErrorState />;
    }
    if (parsed.error) {
      return <ErrorState message={parsed.error} />;
    }

    return (
      <OpenedState
        prompt={parsed.prompt || argPrompt}
        settings={parsed.settings || {}}
        toolCallId={toolCallId}
      />
    );
  },
});

export default GenerateImageToolUI;
